import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SingleDatePicker } from "@/components/shared/SingleDatePicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncated-text";
import { CalendarIcon, ChevronsUpDown, Check } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { DAYS_OF_WEEK } from "@/utils/schedule.util";
import { useState } from "react";

export interface ScheduleFormValues {
  frequency: string;
  dayOfWeek: string;
  dayOfMonth: string;
  startDate: Date;
  timeOfDay: string;
  timezone: string;
  endDate: Date | undefined;
}

interface ScheduleFormFieldsProps {
  values: ScheduleFormValues;
  scheduleType: "one_time" | "recurring";
  showFrequency: boolean;
  timezoneOptions: string[];
  availableHours: string[];
  isDateDisabled: (date: Date) => boolean;
  onFrequencyChange: (value: string) => void;
  onDayOfWeekChange: (value: string) => void;
  onDayOfMonthChange: (value: string) => void;
  onStartDateChange: (date: Date | undefined) => void;
  onTimeOfDayChange: (value: string) => void;
  onTimezoneChange: (value: string) => void;
  onEndDateChange: (date: Date | undefined) => void;
}

export function ScheduleFormFields({
  values,
  scheduleType,
  showFrequency,
  timezoneOptions,
  availableHours,
  isDateDisabled,
  onFrequencyChange,
  onDayOfWeekChange,
  onDayOfMonthChange,
  onStartDateChange,
  onTimeOfDayChange,
  onTimezoneChange,
  onEndDateChange,
}: ScheduleFormFieldsProps) {
  const isRecurring = scheduleType === "recurring";
  const [tzOpen, setTzOpen] = useState(false);
  const showDayOfWeek =
    isRecurring && (values.frequency === "weekly" || values.frequency === "biweekly");
  const showDayOfMonth = isRecurring && values.frequency === "monthly";

  return (
    <>
      {showFrequency && isRecurring && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Frequency</Label>
          <Select value={values.frequency} onValueChange={onFrequencyChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {showDayOfWeek && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Day of Week</Label>
          <Select value={values.dayOfWeek} onValueChange={onDayOfWeekChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OF_WEEK.map((d) => (
                <SelectItem key={d} value={d}>
                  {d.charAt(0).toUpperCase() + d.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showDayOfMonth && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Day of Month</Label>
          <Select value={values.dayOfMonth} onValueChange={onDayOfMonthChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 28 }, (_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>
                  {i + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-medium">
          {scheduleType === "one_time" ? "Run Date" : "Start Date"}
        </Label>
        <SingleDatePicker
          date={values.startDate}
          onDateChange={onStartDateChange}
          disabledDate={isDateDisabled}
          className="w-full"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Time of Day</Label>
        <Select value={values.timeOfDay} onValueChange={onTimeOfDayChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableHours.map((t) => (
              <SelectItem key={t} value={t}>
                {format(new Date(`2000-01-01T${t}:00`), "h:mm a")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Timezone</Label>
        <Popover open={tzOpen} onOpenChange={setTzOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={tzOpen}
              className="w-full justify-between font-normal"
            >
              <TruncatedText>{values.timezone}</TruncatedText>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search timezone..." />
              <CommandList
                className="max-h-[260px] overflow-y-auto"
                onWheel={(e) => {
                  e.stopPropagation();
                  e.currentTarget.scrollTop += e.deltaY;
                }}
              >
                <CommandEmpty>No timezone found.</CommandEmpty>
                {timezoneOptions.map((tz) => (
                  <CommandItem
                    key={tz}
                    value={tz}
                    onSelect={() => {
                      onTimezoneChange(tz);
                      setTzOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        values.timezone === tz ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {tz}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {isRecurring && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">End Date (optional)</Label>
          <SingleDatePicker
            date={values.endDate}
            onDateChange={onEndDateChange}
            disabledDate={(date) => isDateDisabled(date) || date < values.startDate}
            placeholder="No end date"
            className="w-full"
          />
        </div>
      )}
    </>
  );
}
